import { forwardRef } from "react";
import { Download, FileSearch, Loader2, Search } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { CallRow } from "../../../lib/calls";
import type { CrmLabel } from "../../../lib/crmLabels";
import { IconBtn, fmtDuration, fmtNum } from "../../ui";
import { ContextMenu, useContextMenu } from "../../shell/ContextMenu";
import { DensityToggle } from "../_shared/DensityToggle";
import { RailTrigger } from "../_shared/workMobile";
import { useWorkCompact, type WorkDensity } from "../_shared/density";
import { CallFilterMenu } from "./CallFilterMenu";
import { CallItem } from "./CallItem";
import { CallRowItems } from "./CallRowItems";
import { callFiltersActive, type CallFilters } from "./filters";
import { CallThreads, ThreadToggle, useThreadedCalls } from "./threads";

export interface CallTotals {
  today: number;
  todaySeconds: number;
  missed: number;
  averageSeconds: number;
}

/**
 * The middle pane: the number strip, the search head, the scrolling column of
 * calls, and the one context menu they share.
 *
 * A row itself is `CallItem` - it is where the two densities differ in markup
 * rather than in numbers, and keeping that branch out of here leaves this file
 * about the pane.
 */
export const CallList = forwardRef<
  HTMLDivElement,
  {
    rows: CallRow[];
    openId: string | null;
    loading: boolean;
    q: string;
    onQ: (v: string) => void;
    /** Whether the search also reads transcripts, summaries and notes. */
    deep: boolean;
    onDeep: () => void;
    filters: CallFilters;
    onFilters: (next: CallFilters) => void;
    onOpen: (id: string) => void;
    totals: CallTotals;
    labels: CrmLabel[];
    /** Click-to-call is permission-gated and needs telephony configured. */
    canCall: boolean;
    onCallBack: (call: CallRow) => void;
    /** Ask to mark this row's number do-not-call. The section confirms it. */
    onSuppress: (call: CallRow) => void;
    /** Ask to refuse this caller in future; the section confirms it. */
    onBlock: (call: CallRow) => void;
    /** Numbers already blocked from this list this session, so the item stops
     *  being offered on the next row from the same robot. */
    blocked: ReadonlySet<string>;
    onPatch: (id: string, body: { tags: string[] }) => Promise<void>;
    /** Tick a waiting call off, or put it back. */
    onHandled: (call: CallRow, handled: boolean) => void;
    density: WorkDensity;
    onDensity: () => void;
    /** Opens the saved-views rail. Only reachable on a phone, where it is a sheet. */
    onRail: () => void;
    /**
     * The current list as a spreadsheet, ready to be an `href`.
     *
     * Passed in rather than built here: the section owns the query, and a URL
     * assembled a second time in the component that draws the button is a file
     * that quietly stops matching the screen.
     */
    csvHref: string;
  }
>(function CallList(
  {
    rows,
    openId,
    loading,
    q,
    onQ,
    deep,
    onDeep,
    filters,
    onFilters,
    onOpen,
    totals,
    labels,
    canCall,
    onCallBack,
    onSuppress,
    onBlock,
    blocked,
    onPatch,
    onHandled,
    density,
    onDensity,
    onRail,
    csvHref,
  },
  ref,
) {
  const t = useT();
  const menu = useContextMenu<CallRow>();
  // Owned here rather than passed down from the section, unlike the density:
  // this is a property of the list pane alone, nothing else on the screen asks
  // about it, and the section is already the biggest file on this desk.
  const [threaded, toggleThreaded] = useThreadedCalls();
  // The head's top padding is a Tailwind utility, and a utility beats the
  // `crm-` rule that would otherwise tighten it - so the compact value has to
  // be swapped here rather than written in CSS.
  const compact = useWorkCompact();

  return (
    // `crm-work-list` is what the phone layout hides when a call is open.
    <div ref={ref} className="crm-card crm-work-col crm-work-list">
      <div className="crm-cstats">
        <div className="crm-cstat">
          <b>{fmtNum(totals.today)}</b>
          <span>{t("calls.pane.today")}</span>
        </div>
        <div className="crm-cstat">
          <b>{fmtDuration(totals.todaySeconds)}</b>
          <span>{t("calls.pane.duration")}</span>
        </div>
        <div className="crm-cstat">
          <b>{fmtNum(totals.missed)}</b>
          <span>{t("calls.pane.missed")}</span>
        </div>
        <div className="crm-cstat">
          <b>{fmtDuration(totals.averageSeconds)}</b>
          <span>{t("calls.pane.average")}</span>
        </div>
      </div>

      <div className={cn("crm-pane-head", compact ? "pt-2.5" : "pt-3.5")}>
        <RailTrigger label={t("calls.pane.views")} onOpen={onRail} />
        <label className="crm-pane-search">
          <Search className="size-4" />
          <input
            type="search"
            value={q}
            onChange={(e) => onQ(e.target.value)}
            placeholder={t("calls.pane.searchPlaceholder")}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        {loading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
        {/* Next to the box it widens rather than inside the filter menu: this
            changes what the words are matched against, and the filter dot is
            what tells an operator their list is narrowed. Disabled with an
            empty box, where it would have nothing to widen. */}
        <IconBtn
          className={cn(deep && q && "crm-cb-dot", deep && "is-on")}
          title={t("calls.pane.deepHint")}
          aria-label={t("calls.pane.deep")}
          aria-pressed={deep}
          disabled={!q}
          onClick={onDeep}
        >
          <FileSearch className="size-4" />
        </IconBtn>
        {/* An anchor, not a button: the endpoint is same-origin and cookie
            authed, so the browser's own download does the whole job and there is
            no blob to build or revoke. `download` keeps the file rather than
            navigating the pane away from the desk. */}
        <a
          className="crm-cb"
          href={csvHref}
          download
          title={t("calls.pane.exportHint")}
          aria-label={t("calls.pane.export")}
        >
          <Download className="size-4" />
        </a>
        <ThreadToggle threaded={threaded} onToggle={toggleThreaded} />
        <DensityToggle density={density} onToggle={onDensity} ns="calls" />
        <CallFilterMenu filters={filters} onFilters={onFilters} />
      </div>

      {/* `is-grouped` widens every row by the chevron's lane, plain rows too,
          so the tiles stay on one line. See `crm-folded-rows.css`. */}
      <div className={cn("crm-pane-scroll", threaded && "is-grouped")}>
        {rows.length === 0 ? (
          // Saying "no calls yet" over a filtered list would send an operator
          // looking for a telephony fault that is not there.
          <div className="crm-pane-empty">
            {callFiltersActive(filters) ? t("calls.filter.noHits") : t("calls.pane.empty")}
          </div>
        ) : threaded ? (
          <CallThreads
            rows={rows}
            openId={openId}
            labels={labels}
            onOpen={onOpen}
            rowMenuProps={menu.rowProps}
          />
        ) : (
          rows.map((call) => (
            <CallItem
              key={call.id}
              call={call}
              open={call.id === openId}
              labels={labels}
              onOpen={onOpen}
              menuProps={menu.rowProps(call)}
            />
          ))
        )}
      </div>

      {/* One panel for the whole list, holding whichever row was right-clicked.
          It renders into the body, so the pane's clipped overflow cannot cut it
          off the way an absolutely positioned panel in the last row would be. */}
      <ContextMenu menu={menu} label={t("calls.row.menu")}>
        {(call, close) => (
          <CallRowItems
            call={call}
            labels={labels}
            canCall={canCall}
            close={close}
            onOpen={onOpen}
            onCallBack={onCallBack}
            onSearch={onQ}
            onSuppress={onSuppress}
            onBlock={onBlock}
            blocked={blocked}
            onPatch={onPatch}
            onHandled={onHandled}
          />
        )}
      </ContextMenu>
    </div>
  );
});
